import { Host, h } from "@stencil/core";
import { attachSlottedCss, checkSlotContent, createUuid, injectCSS, injectFont, observeOptions, } from "../../utils";
import { createStyles, getSlottedCss } from "./dropdown-wrapper-styles";
import { closeOtherDropdowns, addDocumentEventListeners, removeDocumentEventListeners, } from "./dropdown-wrapper-utils";
import { StateMessage } from "../common/state-message/state-message";
export class DcUiDropdownWrapper {
    constructor() {
        this.label = '';
        this.labelPosition = 'default';
        this.labelWidth = '25%';
        this.disabled = false;
        this.validationstate = 'none';
        this.message = '';
        this.open = false;
        this.selectedOptionIndex = null;
        this.currentSearchIndex = 0;
        this.useNativeSelect = false;
        this.searchBuffer = '';
        this.searchTimer = null;
        this.loadOptions = () => {
            if (!this.nativeSelectEl) {
                return;
            }
            this.listEl.innerHTML = '';
            const optGroups = this.nativeSelectEl.querySelectorAll('optgroup');
            if (optGroups.length) {
                this.listEl.classList.add('has-optgroups');
                Array.from(optGroups).forEach((optGroup) => {
                    this.addOptGroup(optGroup);
                });
            }
            else {
                const ul = document.createElement('ul');
                ul.setAttribute('role', 'listbox');
                ul.setAttribute('aria-labelledby', `${this.uuid}_label`);
                this.listEl.appendChild(ul);
                Array.from(this.nativeSelectEl.options).forEach((nativeOptionEl) => {
                    this.addOption(nativeOptionEl, ul);
                });
            }
            this.updateSelectedOptionFromNativeSelect();
        };
        this.addOptGroup = (optGroup) => {
            const optGroupEl = document.createElement('ul');
            optGroupEl.setAttribute('role', 'listbox');
            optGroupEl.setAttribute('aria-labelledby', `${this.uuid}_label`);
            this.listEl.appendChild(optGroupEl);
            optGroupEl.setAttribute('role', 'group');
            const optGroupHeadingEl = document.createElement('li');
            optGroupEl.appendChild(optGroupHeadingEl);
            optGroupHeadingEl.setAttribute('role', 'presentation');
            optGroupHeadingEl.classList.add('dropdown-optgroup-heading');
            optGroupHeadingEl.innerHTML = optGroup.label;
            const index = this.listEl.children.length - 1;
            optGroupHeadingEl.id = `${this.uuid}_optgroup-${index}`;
            optGroupEl.setAttribute('aria-labelledby', optGroupHeadingEl.id);
            Array.from(optGroup.children).forEach((nativeOptionEl) => {
                this.addOption(nativeOptionEl, optGroupEl);
            });
        };
        this.addOption = (nativeOptionEl, parent) => {
            const index = this.listEl.querySelectorAll('.dropdown-option').length;
            nativeOptionEl.setAttribute(`data-${this.uuid}-index`, `${index}`);
            const optionEl = document.createElement('li');
            optionEl.classList.add('dropdown-option');
            optionEl.innerHTML = nativeOptionEl.innerHTML;
            optionEl.id = `${this.uuid}_option-${index}`;
            optionEl.setAttribute(`data-${this.uuid}-index`, `${index}`);
            optionEl.setAttribute('role', 'option');
            optionEl.setAttribute('aria-selected', 'false');
            const icon = document.createElement('dc-ui-icon');
            icon.classList.add('checkmark-icon');
            icon.setAttribute('name', 'dc_checkmark');
            icon.setAttribute('size', 'tiny');
            icon.setAttribute('color', 'darkBlue02');
            optionEl.appendChild(icon);
            optionEl.addEventListener('click', () => {
                this.selectOption(optionEl);
            });
            parent.appendChild(optionEl);
        };
        this.observeNativeSelectOptions = () => {
            observeOptions(this.nativeSelectEl, () => {
                this.loadOptions();
            });
        };
        this.updateSelectedOptionFromNativeSelect = () => {
            const selectedNativeOptionEl = this.nativeSelectEl.querySelector('option:checked');
            if (selectedNativeOptionEl) {
                const index = +selectedNativeOptionEl.getAttribute(`data-${this.uuid}-index`);
                const selectedOptionEl = this.optionElByIndex(index);
                this.selectOption(selectedOptionEl, false);
            }
        };
        this.selectOption = (optionEl, fireEvent = true) => {
            Array.from(this.listEl.querySelectorAll('.dropdown-option')).forEach((otherOptionEl) => {
                otherOptionEl.setAttribute('aria-selected', 'false');
            });
            optionEl.setAttribute('aria-selected', 'true');
            this.headTextEl.innerHTML = optionEl.textContent;
            this.selectedOptionIndex = +optionEl.getAttribute(`data-${this.uuid}-index`);
            this.nativeSelectEl.value = this.nativeOptionElByIndex(this.selectedOptionIndex).value;
            if (fireEvent) {
                this.nativeSelectEl.dispatchEvent(new Event('change', { bubbles: true })); // bubbles: true is required for the event to be caught in react
            }
        };
        this.onHeadKeydown = (e) => {
            // Handle space/enter key
            if (['SpaceBar', 'Enter', ' '].includes(e.key)) {
                e.preventDefault(); // Prevent page scroll on space
                if (this.open) {
                    // If dropdown is open, handle selection
                    const activeOption = this.listEl.querySelector('.dropdown-option.active');
                    if (activeOption) {
                        this.selectOption(activeOption);
                        this.open = false; // Close after selection
                    }
                }
                else {
                    // If dropdown is closed, open it
                    this.open = true;
                    if (this.selectedOptionIndex !== null) {
                        // Highlight the currently selected option when opening
                        this.highlightSelectedOption();
                    }
                }
                return;
            }
            // Only handle other keyboard interactions if dropdown is open
            if (!this.open)
                return;
            // Handle arrow key navigation (only highlight, don't select)
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault();
                const offset = ['ArrowDown', 'ArrowRight'].includes(e.key) ? 1 : -1;
                const currentActive = this.listEl.querySelector('.dropdown-option.active');
                const currentIndex = currentActive
                    ? +currentActive.getAttribute(`data-${this.uuid}-index`)
                    : this.selectedOptionIndex;
                const nextIndex = currentIndex + offset;
                const nextOptionEl = this.optionElByIndex(nextIndex);
                if (nextOptionEl) {
                    // Remove active class from all options
                    Array.from(this.listEl.querySelectorAll('.dropdown-option')).forEach((optionEl) => {
                        optionEl.classList.remove('active');
                    });
                    // Add active class to next option
                    nextOptionEl.classList.add('active');
                }
                return;
            }
            // Handle backspace to allow search correction
            if (e.key === 'Backspace') {
                e.preventDefault();
                this.searchBuffer = this.searchBuffer.slice(0, -1);
                this.currentSearchIndex = 0;
                if (this.searchBuffer) {
                    this.performSearch();
                }
                return;
            }
            // Handle character input
            if (e.key.length === 1) {
                e.preventDefault();
                // Clear previous timer if it exists
                if (this.searchTimer) {
                    clearTimeout(this.searchTimer);
                }
                const lastChar = e.key.toLowerCase();
                // If typing the same character as last time, cycle through matches
                if (this.searchBuffer && lastChar === this.searchBuffer[this.searchBuffer.length - 1]) {
                    this.currentSearchIndex =
                        (this.currentSearchIndex + 1) % this.listEl.querySelectorAll('.dropdown-option').length;
                    this.performSearch();
                }
                else {
                    // New character sequence, append to buffer
                    this.searchBuffer += lastChar;
                    this.currentSearchIndex = 0;
                    this.performSearch();
                }
                // Clear the search buffer after 500ms of no input
                this.searchTimer = setTimeout(() => {
                    this.searchBuffer = '';
                    this.currentSearchIndex = 0;
                }, 500);
            }
        };
        this.performSearch = () => {
            const options = Array.from(this.listEl.querySelectorAll('.dropdown-option'));
            // Find all options that match the search buffer
            const matchingOptions = options.filter((option) => option.textContent.trim().toLowerCase().startsWith(this.searchBuffer));
            if (matchingOptions.length > 0) {
                // Get the current matching option
                const matchingOption = matchingOptions[this.currentSearchIndex % matchingOptions.length];
                // Remove active class from all options
                options.forEach((optionEl) => {
                    optionEl.classList.remove('active');
                });
                // Only highlight the matching option visually
                matchingOption.classList.add('active');
            }
        };
        this.highlightSelectedOption = () => {
            if (this.selectedOptionIndex !== null) {
                const selectedOption = this.optionElByIndex(this.selectedOptionIndex);
                if (selectedOption) {
                    // Remove active class from all options first
                    Array.from(this.listEl.querySelectorAll('.dropdown-option')).forEach((optionEl) => {
                        optionEl.classList.remove('active');
                    });
                    // Add active class to selected option
                    selectedOption.classList.add('active');
                }
            }
        };
        this.onHeadClick = (e) => {
            e.stopPropagation();
            if (this.disabled) {
                return;
            }
            closeOtherDropdowns(this);
            if (!this.useNativeSelect) {
                this.open = !this.open;
                // Highlight and scroll to selected option when opening dropdown
                if (this.open) {
                    this.highlightSelectedOption();
                }
            }
        };
        this.optionElByIndex = (index) => {
            return this.listEl.querySelector(`[data-${this.uuid}-index="${index}"]`);
        };
        this.nativeOptionElByIndex = (index) => {
            return this.nativeSelectEl.querySelector(`option[data-${this.uuid}-index="${index}"]`);
        };
    }
    handleDocumentKeydown(event) {
        if (event.key === 'Tab' && this.open) {
            this.open = false;
        }
    }
    componentWillLoad() {
        injectFont();
        checkSlotContent(this.el, 'select');
        this.uuid = createUuid();
        this.nativeSelectEl = this.el.querySelector('select');
        if (!this.nativeSelectEl) {
            console.warn('dropdown has no select element');
            return;
        }
        // if on a mobile (touch) device, use the native element instead of the custom dropdown
        this.useNativeSelect = 'ontouchstart' in window;
        if (this.useNativeSelect) {
            return;
        }
        // if using the custom dropdown, synchronize it with the native element (in case of external changes)
        this.nativeSelectEl.setAttribute('aria-hidden', 'true');
        this.nativeSelectEl.addEventListener('change', () => {
            this.updateSelectedOptionFromNativeSelect();
        });
        this.observeNativeSelectOptions();
    }
    componentDidLoad() {
        if (!this.nativeSelectEl) {
            return;
        }
        if (this.useNativeSelect) {
            attachSlottedCss(this.el, getSlottedCss(this.el, this.useNativeSelect, this.labelPosition, this.validationstate));
            if (this.disabled) {
                this.nativeSelectEl.setAttribute('disabled', 'true');
            }
            return;
        }
        // if using the custom dropdown, load the options from the native element
        // and watch for external changes to them
        this.loadOptions();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        this.selectObserver = new MutationObserver((_mutations, _observer) => {
            this.loadOptions();
        });
        this.selectObserver.observe(this.nativeSelectEl, {
            attributes: false,
            childList: true,
            subtree: true,
        });
        addDocumentEventListeners(this);
    }
    connectedCallback() {
        this.observeNativeSelectOptions();
    }
    disconnectedCallback() {
        if (this.useNativeSelect) {
            return;
        }
        removeDocumentEventListeners(this);
    }
    render() {
        injectCSS(this.el, createStyles, this.labelPosition, this.disabled, this.useNativeSelect, this.validationstate, this.labelWidth, this.open);
        const arrowIconColor = this.disabled ? 'grey03' : 'darkBlue02';
        const label = (h("label", { key: '1322be57bdbfeed3fe34dee49fc607aa193d01c9', id: `${this.uuid}_label`, "aria-hidden": this.labelPosition === 'default' ? 'true' : 'false' }, this.label));
        return (h(Host, { key: 'ba2986222e0cd733d9058b3a1ce1cf68e0326030', "data-uuid": this.uuid }, !this.useNativeSelect && h("slot", { key: 'ebc382d712839132d2b9ef15ee199a51d0d4e9ef' }), h("div", { key: 'e17b5ffc004f2c189850203b1c60690074393acb', class: "container" }, this.labelPosition !== 'default' && label, this.useNativeSelect ? (h("div", { ref: (el) => (this.nativeSelectWrapperEl = el), class: {
                'native-select-wrapper': true,
                'dropdown-head': true,
                disabled: this.disabled,
                error: this.validationstate === 'error',
            } }, this.labelPosition === 'default' && label, h("slot", null), h("dc-ui-icon", { class: "arrow-icon", name: "dc_down", size: "tiny", color: arrowIconColor }))) : (h("div", { class: "dropdown", ref: (el) => (this.dropdownEl = el) }, h("button", { ref: (el) => (this.headEl = el), class: {
                'dropdown-head': true,
                disabled: this.disabled,
                error: this.validationstate === 'error',
            }, tabindex: this.disabled ? '-1' : '0', role: "combobox", "aria-controls": `${this.uuid}_list`, "aria-expanded": this.open ? 'true' : 'false', "aria-haspopup": "listbox", "aria-labelledby": `${this.uuid}_label`, "aria-activedescendant": `${this.uuid}_option-${this.selectedOptionIndex}`, "aria-live": this.open ? 'off' : 'polite', onClick: this.onHeadClick, onKeyDown: this.onHeadKeydown }, this.labelPosition === 'default' && label, h("span", { ref: (el) => (this.headTextEl = el), class: "dropdown-head-text", tabindex: "-1" }), h("dc-ui-icon", { class: "arrow-icon", name: "dc_down", size: "tiny", color: arrowIconColor })), h("div", { id: `${this.uuid}_list`, ref: (el) => (this.listEl = el), tabindex: "-1", class: {
                'dropdown-list': true,
                open: this.open,
            } }))), this.validationstate !== 'none' && (h(StateMessage, { key: 'af9ad30ed2b6e9510e07a256d1e7cedb9f6990b1', state: this.validationstate, message: this.message })))));
    }
    static get is() { return "dc-ui-dropdown-wrapper"; }
    static get encapsulation() { return "shadow"; }
    static get properties() {
        return {
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "label",
                "reflect": false,
                "defaultValue": "''"
            },
            "labelPosition": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "TDropdownLabelPosition",
                    "resolved": "\"above\" | \"default\" | \"left\"",
                    "references": {
                        "TDropdownLabelPosition": {
                            "location": "import",
                            "path": "./dropdown-wrapper-utils",
                            "id": "src/components/dc-ui-dropdown-wrapper/dropdown-wrapper-utils.ts::TDropdownLabelPosition"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "label-position",
                "reflect": false,
                "defaultValue": "'default'"
            },
            "labelWidth": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "BreakpointCustomizable<string>",
                    "resolved": "string | { base?: string; xs?: string; sm?: string; md?: string; lg?: string; xl?: string; } & { base: string; }",
                    "references": {
                        "BreakpointCustomizable": {
                            "location": "import",
                            "path": "../../utils",
                            "id": "src/utils/index.ts::BreakpointCustomizable"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "label-width",
                "reflect": false,
                "defaultValue": "'25%'"
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "validationstate": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "TValidationState",
                    "resolved": "\"error\" | \"none\"",
                    "references": {
                        "TValidationState": {
                            "location": "import",
                            "path": "../../utils",
                            "id": "src/utils/index.ts::TValidationState"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "validationstate",
                "reflect": false,
                "defaultValue": "'none'"
            },
            "message": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "message",
                "reflect": false,
                "defaultValue": "''"
            }
        };
    }
    static get states() {
        return {
            "open": {},
            "selectedOptionIndex": {},
            "currentSearchIndex": {}
        };
    }
    static get elementRef() { return "el"; }
    static get listeners() {
        return [{
                "name": "keydown",
                "method": "handleDocumentKeydown",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=dc-ui-dropdown-wrapper.js.map
